<script lang="ts" setup>
import type { RoutesNamesList } from '@typed-router/__routes'
import { StorageDownloadCache, StorageGetCache, StorageListCache } from '@d/cache'
import { getDensityClass, getDensityIconSize } from '@d/form/components/tree/utree'
import UTree from '@d/form/components/tree/UTree.vue'
import { websocket } from '@d/sdk'
import { deepEqual } from '@d/util'
import { resolveClientSyncWaiters } from '@d/util/ws'
import { mdiFolder, mdiFolderOpen, mdiFolderRemove, mdiLoading, mdiRefresh, mdiFolderOpenOutline, mdiCloud, mdiCloudOutline, mdiCloudOffOutline, mdiInformationOutline, mdiInformationOffOutline, mdiPencil, mdiFileChart, mdiSizeM, mdiSizeS, mdiSizeL, mdiFolderAccount } from '@mdi/js'
import { filesize } from 'filesize'
import { useTreeView } from '../../composables/useTreeView'
import { viewModeFilter } from '../../helper/filter'
import { icon } from '../../helper/icon'
import { density, totalFolderCount } from '../../helper/localstorage'
import { useStateScope } from '../../helper/shortcut'
import { getTimeString } from '../../helper/time'
import Shortcut from '../util/Shortcut.vue'
import { handleFileSelect } from './helper/fileSelect'
import { fetchPath, fsHierarchySorted, initialLoad, itemFilter, opened, selected, treeItems, viewMode } from './treeMapper'

interface Props {
  disableAnimations?: boolean
}

const _props = withDefaults(defineProps<Props>(), {
  disableAnimations: false,
})

const router = useRouter()
const route = useRoute('client-view-path')
const treeView = useTreeView()
const showFileInformation = useLocalStorage('showFileInformation', true)
const { t, locale } = useI18n({
  useScope: 'local',
})

const path = computed(() => {
  return route.params.path ?? []
})

const showContextMenu = ref(false)
const refreshLoading: Ref<boolean> = ref(false)
// Workaround: treeview returns in case of template-item Symbol instead of itemFullpath
const cache = ref<string[]>([])

const utree = templateRef('utree')

async function refresh(extra?: string[], cancelInvalidate = false): Promise<void> {
  console.log('Triggered refresh')
  refreshLoading.value = true
  await initialLoad([path.value, ...opened.value.filter(e => e).map(e => e.split('/')), extra ?? []], !cancelInvalidate)
  refreshLoading.value = false
  document.dispatchEvent(new CustomEvent('tree-refresh'))
  utree.value?.slideIntoView()
}

function contextMenuClicked(): void {
  showContextMenu.value = true
}

function handleSelect(current: string[]): void {
  const alreadySelected = selected.value[0] === current[0]
  selected.value = current
  cache.value = current
  if (alreadySelected) {
    // @update:selected won't fire when the value hasn't changed (e.g. after returning
    // from a different page), so trigger navigation directly.
    triggerSelect(current, true, 'client-view-path')
  }
}

function handleContextMenu(current: string[]): void {
  const routeData = router.resolve({ name: 'client-view-path', params: { client: route.params.client, path: current } })
  window.open(routeData.href.replaceAll('%2F', '/'), '_blank')
}

async function triggerSelect(newSelected: string[] | string, preserveHash?: boolean, customRoute?: RoutesNamesList): Promise<void> {
  const path = (typeof newSelected === 'string') ? newSelected.split('/') : newSelected.at(0)!.split('/')
  const hash = preserveHash ? route.hash : undefined
  await handleFileSelect(route.params.client, path, hash, customRoute ?? route.name)
}

function openFoldersTo(selectedPath: string[]): void {
  const folderSet = new Set<string>(opened.value)
  folderSet.add(decodeURIComponent(selectedPath[0]))
  let lastPath = decodeURIComponent(selectedPath[0])
  for (let i = 1; i < selectedPath.length; i++) {
    if (decodeURIComponent(selectedPath[i]).includes('.') && i === selectedPath.length - 1)
      continue // dont add files to the opened list
    const folderPath = `${lastPath}/${decodeURIComponent(selectedPath[i])}`
    folderSet.add(folderPath)
    lastPath = folderPath
  }
  opened.value = Array.from(folderSet)
}

function getDensityIcon(): string {
  switch (density.value) {
    case 'comfortable':
      return mdiSizeM
    case 'compact':
      return mdiSizeS
    case 'default': default:
      return mdiSizeL
  }
}

function setDensity() {
  switch (density.value) {
    case 'comfortable':
      density.value = 'compact'
      break
    case 'compact':
      density.value = 'default'
      break
    case 'default': default:
      density.value = 'comfortable'
      break
  }
}

function setTotalFolderCount() {
  totalFolderCount.value = !totalFolderCount.value
}

function getTotalFolderIcon() {
  return totalFolderCount.value ? mdiFolderAccount : mdiFolder
}

watch(path, async (newPath, oldPath) => {
  if (oldPath && newPath && (deepEqual(newPath, oldPath) || newPath.length === 0))
    return

  selected.value = [newPath.join('/')]
  openFoldersTo(path.value)
  await initialLoad([path.value], false, true)
  utree.value?.slideIntoView()
})

onMounted(async () => {
  refreshLoading.value = true
  // await until(() => StorageListCache.get(path.value.join('/')) !== undefined)
  // if (treeItems.value.length === 0) {
  if (path.value.length > 0) {
    selected.value = [path.value.join('/')]
    openFoldersTo(path.value)
  }

  await initialLoad([path.value])
  // }
  // used to open folders and navigate to node
  refreshLoading.value = false

  // set last state in case of return from other view
  if (selected.value.length > 0) {
    triggerSelect(selected.value, true)
  }
})

// StorageListCache.on('afterInvalidate', reloadCheck)

// onUnmounted(() => {
//   StorageListCache.off('afterInvalidate', reloadCheck)
// })

/*
let shouldRefresh = false

function reloadCheck(key: string, value: StorageNode[], old: StorageNode[], reload: boolean): void {
  if (reload && !shouldRefresh && !refreshLoading.value) {
    shouldRefresh = true
    // Prevent multiple refreshes in the same tick
    nextTick(() => {
      if (shouldRefresh) {
        console.log('Refreshing tree after invalidation')
        shouldRefresh = false
        refresh(undefined, true)
      }
    })
  }
}
*/

websocket.on('sync', (paths: string[]) => {
  console.log('WS sync', paths)
  paths.forEach((path) => {
    StorageListCache.invalidate(path)
    StorageGetCache.invalidate(path)
    StorageDownloadCache.invalidate(path)
    resolveClientSyncWaiters(path)
  })
})

/* watch(selected, (newSelected, oldSelected) => {
  if (newSelected.length === 0 || oldSelected[0] === newSelected[0])
    return
  const selectedPath = newSelected[0].split('/')
  openFoldersTo(selectedPath.slice(0, selectedPath.length - 1))
  if (oldSelected.length === 0 && newSelected.length > 0) {

  }
  else {
    console.log('N:', newSelected.join('/'))
    console.log('O:', oldSelected.join('/'))
    triggerSelect(newSelected, preserveHashOnce)
  }
  preserveHashOnce = false
}, { deep: true }) */

watch(treeView.internal.treeRefresh, (x) => {
  if (x) {
    refresh(Array.from(treeView.internal.extra.value))
    treeView.internal.extra.value = []
  }
})

function getFileName(name: string): string {
  if (viewMode.value) {
    const idx = name.lastIndexOf('.')
    if (idx !== -1) {
      return name.substring(0, idx)
    }
  }
  return name
}

const densityClass = computed(() => getDensityClass(density.value))
const iconSize = computed(() => getDensityIconSize(density.value))

const scrollboxEl = templateRef('scrollboxEl')
const scrollboxFocus = useFocusWithin(scrollboxEl)

// function treeHasFocus(): boolean {
//   const activeEl = document.activeElement as HTMLElement | null
//   if (activeEl) {
//     return activeEl.closest('.tree-view-wrapper') !== null
//   }
//   return false
// }

// let lastFocus = null as HTMLElement | null

const state = useStateScope()

watch(scrollboxFocus.focused, (focused) => {
  if (state)
    state.treeFocus.value = focused
})

function focusTree() {
  // const focused = treeHasFocus()

  // if (focused) {
  //   lastFocus?.focus()
  //   return
  // }
  // lastFocus = document.activeElement as HTMLElement | null
  openFoldersTo(path.value)

  nextTick(() => {
    const activeEl = document.querySelector('.tree-view-wrapper .v-list-item--active') as HTMLElement | null
    if (activeEl) {
      activeEl.focus()
      setTimeout(() => {
        scrollTo(activeEl)
        // activeEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
      }, 400) // Wait for animations
    }
  })
}

// function focusTree(top: boolean) {
//   const activeEl = document.querySelector('.tree-view-wrapper .v-list-item--active') as HTMLElement | null
//   if (activeEl) {
//     activeEl.scrollIntoView({ behavior: 'smooth', block: top ? 'start' : 'center' })
//     activeEl.focus()
//   }
// }

function scrollTo(el: HTMLElement) {
  if (!el)
    return
  console.log('scroll to called')
  const behavior = _props.disableAnimations ? 'instant' : 'smooth'
  const dimensions = scrollboxEl.value?.getBoundingClientRect()
  if (!dimensions)
    return
  const elDimensions = el.getBoundingClientRect()
  const offsetTop = elDimensions.top - dimensions.top + (scrollboxEl.value?.scrollTop || 0)
  const offsetBottom = elDimensions.bottom - dimensions.bottom + (scrollboxEl.value?.scrollTop || 0)
  // Compute content coordinates and decide scroll behavior.
  const curLeft = scrollboxEl.value?.scrollLeft || 0
  const viewLeft = dimensions.x
  const viewWidth = dimensions.width || 0
  const elLeft = elDimensions.x
  const elWidth = elDimensions.width || 0

  // element left in content coordinates
  const elLeftContent = curLeft + (elLeft - viewLeft)
  const maxScrollLeft = Math.max(0, (scrollboxEl.value?.scrollWidth || 0) - viewWidth)
  const maxVisible = Math.min(150, viewWidth)

  const desiredMax = elLeftContent - (viewWidth - maxVisible)

  let targetLeft = curLeft

  if (elLeftContent < curLeft) {
    targetLeft = Math.max(0, Math.min(maxScrollLeft, desiredMax))
  }
  else if ((elLeftContent + elWidth) > (curLeft + viewWidth)) {
    targetLeft = Math.max(0, Math.min(maxScrollLeft, desiredMax))
  }

  const offsetLeft = targetLeft
  if (offsetTop < (scrollboxEl.value?.scrollTop || 0)) {
    scrollboxEl.value?.scrollTo({ top: offsetTop, left: offsetLeft, behavior })
    return
  }
  if (offsetBottom > ((scrollboxEl.value?.scrollTop || 0) + (dimensions.height || 0))) {
    scrollboxEl.value?.scrollTo({ top: offsetBottom - (dimensions.height || 0), left: offsetLeft, behavior })
    return
  }

  scrollboxEl.value?.scrollTo({ left: offsetLeft, behavior })
}

function jumpTree(up: boolean) {
  const el = document.querySelectorAll(`.tree-view-wrapper .v-list-item`) as NodeListOf<HTMLElement>
  const selected = el[up ? 0 : el.length - 1]!
  if (selected) {
    scrollTo(selected)
    // selected.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    selected.focus()
  }
}

function focusLevelAbove() {
  const activeEl = document.activeElement as HTMLElement | null
  if (activeEl) {
    const levelEl = activeEl.closest('.v-list-group--open') as HTMLElement | null
    if (levelEl) {
      const levelItem = levelEl.querySelector('.v-list-item') as HTMLElement | null
      if (levelItem) {
        scrollTo(levelItem)
        // levelItem.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
        levelItem.focus()
      }
    }
  }
}
</script>

<template>
  <v-container fluid class="toolbar">
    <Shortcut keys="pagedown" :action="() => jumpTree(false)" :disabled="!scrollboxFocus.focused.value" prevent-default hidden />
    <Shortcut keys="pageup" :action="() => jumpTree(true)" :disabled="!scrollboxFocus.focused.value" prevent-default hidden />
    <Shortcut keys="alt+a" :action="() => focusTree()" prevent-default hidden />
    <v-row class="pa-2" style="display: flex; justify-content: space-between">
      <div>
        <v-tooltip>
          <template #activator="{ props }">
            <v-btn
              :disabled="refreshLoading"
              class="mr-1" size="x-small" :loading="refreshLoading" icon v-bind="props"
              @click="refresh()"
            >
              <v-icon :icon="mdiRefresh" size="x-large" />
            </v-btn>
          </template>
          {{ t('refresh') }}
        </v-tooltip>
        <v-tooltip>
          <template #activator="{ props }">
            <v-btn
              class="mr-1" size="x-small" icon v-bind="props"
              @click="showFileInformation = !showFileInformation"
            >
              <v-icon :icon="showFileInformation ? mdiInformationOutline : mdiInformationOffOutline" size="x-large" />
            </v-btn>
          </template>
          {{ t('showFileInformation') }}
        </v-tooltip>
        <v-tooltip>
          <template #activator="{ props }">
            <v-btn
              size="x-small" v-bind="props"
              class="mr-2"
              icon
              @click="setDensity()"
            >
              <v-icon :icon="getDensityIcon()" size="x-large" />
            </v-btn>
          </template>
          {{ t('elementSize') }}
        </v-tooltip>
        <v-tooltip>
          <template #activator="{ props }">
            <v-btn
              size="x-small" v-bind="props"
              icon
              @click="setTotalFolderCount()"
            >
              <v-icon :icon="getTotalFolderIcon()" size="x-large" />
            </v-btn>
          </template>
          {{ t('totalFolderCount') }}
        </v-tooltip>
      </div>
      <div style="display: flex; align-items: center;">
        <v-btn
          class="mr-1" size="small"
          variant="outlined"
          @click="viewMode = !viewMode"
        >
          <span>{{ t(`raw.${!viewMode}`) }}</span>
        </v-btn>
      </div>
    </v-row>
  </v-container>
  <div ref="scrollboxEl" class="treeview-scrollbox" style="flex-grow: 1; overflow: auto;">
    <div v-if="refreshLoading" class="loading-container">
      <v-progress-circular
        :size="70"
        color="primary"
        indeterminate
      />
    </div>
    <UTree
      v-else-if="(treeItems.length > 0)"
      ref="utree"
      v-model:opened="opened"
      v-model:selected="selected"
      style="overflow: revert"
      :items="fsHierarchySorted"
      :load-children="fetchPath"
      :item-filter="itemFilter"
      :disable-animations="disableAnimations"
      :expand-icon="mdiFolder"
      :collapse-icon="mdiFolderOpen"
      :loading-icon="mdiLoading"
      :storage-icon="mdiCloud"
      :storage-opened-icon="mdiCloudOutline"
      :storage-error-icon="mdiCloudOffOutline"
      :error-icon="mdiFolderRemove"
      :empty-icon="mdiFolderOpenOutline"
      item-title="name"
      item-value="fullPath"
      item-error="error"
      item-type="type"
      :density="density"
      :scroll-to="scrollTo"
      @contextmenu.prevent="contextMenuClicked"
      @update:selected="(selected) => triggerSelect(selected, undefined, 'client-view-path')"
    >
      <template #item="{ it: item, style }">
        <span v-if="!viewMode || viewModeFilter(item, selected[0])" class="item">
          <v-list-item
            :class="densityClass"
            :active="item.fullPath === selected[0]"
            :value="item.fullPath"
            :title="getFileName(item.name)"
            slim
            :density="density"
            :style="style"
            @click="handleSelect([item.fullPath])"
            @click.middle="handleContextMenu([item.fullPath])"
            @keydown.left.prevent="focusLevelAbove"
            @keydown.right.prevent="void 0"
          >
            <template #prepend>
              <div :class="densityClass">
                <v-icon :icon="icon(item, !item.fullPath.includes('/'))" color="grey" :size="iconSize" />
              </div>
            </template>
            <template v-if="showFileInformation" #subtitle>
              <div class="no-compact-style">
                <div class="d-flex" style="gap:4px;">

                  <span :class="densityClass">
                    <v-chip density="comfortable" size="x-small" :prepend-icon="mdiPencil">{{ getTimeString(locale, item.lastModified, 'dd.MM.yyyy HH:mm:ss') }}</v-chip>
                  </span>
                  <span :class="densityClass">
                    <v-chip density="comfortable" size="x-small" :prepend-icon="mdiFileChart">{{ filesize(item.size ?? 0, { standard: "jedec" }) }}</v-chip>
                  </span>
                </div>
              </div>
            </template>
            <template #append>
              <Shortcut v-if="item.fullPath === selected[0] && !state?.treeFocus.value" keys="Alt+A" only-display />
            </template>

          </v-list-item>
        </span>
        <div v-else />
      </template>
    </UTree>
    <div v-else class="empty-storage-container">
      <v-icon size="120" color="grey lighten-2" :icon="mdiCloudOutline" />
      <span class="text-h6">
        {{ t('noStorages') }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.treeview-scrollbox {
  border: 4px dashed transparent;
}

.treeview-scrollbox:focus-within {
  border: 4px solid rgba(var(--v-theme-primary), 0.2);
}

.toolbar {
  display: flex;
  vertical-align: middle;
  justify-content: space-between;
  background-color: rgba(var(--v-theme-primary), 0.1);
  border-bottom: 1px solid rgba(var(--v-theme-primary), 0.15);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
}

.empty-storage-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  text-align: center;
}

.icon-container {
  display: flex;
  height:100%;
  width: 100%;
}

.density-default :deep(.v-list-item-title),
.density-default :deep(.v-list-item-subtitle) {
  padding-left: 12px;
}

.density-comfortable :deep(.v-list-item-title),
.density-comfortable :deep(.v-list-item-subtitle) {
  padding-left: 8px;
}

.density-compact :deep(.v-list-item-title),
.density-compact :deep(.v-list-item-subtitle) {
  padding-left: 4px;
}
</style>

<i18n lang="yaml">
  de:
    refresh: Aktualisieren
    rage: Storage registrieren
    create: Erstellen
    raw:
      false: ViewMode
      true: RawMode
    noStorages: Keine Storages verknüpft
    size: 'Größe: '
    lastModified: 'Zuletzt geändert: '
    showFileInformation: Dateiinformationen anzeigen
    elementSize: Größe der Elemente im Baum
  en:
    refresh: Refresh
    create: Create
    raw:
      false: ViewMode
      true: RawMode
    noStorages: No Storages linked
    size: 'Size: '
    lastModified: 'Last modified: '
    showFileInformation: Show File information
    elementSize: Size of elements in tree
</i18n>
